import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { formatClassNames } from '@wix/editor-elements-common-utils';
import Link from '@wix/thunderbolt-elements/components/Link';
import { TestIds } from '../constants';
import type {
  TextParticlesImperativeActions,
  TextParticlesProps,
} from '../TextParticles.types';
import semanticClassNames from '../TextParticles.semanticClassNames';
import { useComputedStyle } from './hooks/useComputedStyle';
import { useImageLoader } from './hooks/useImageLoader';
import { useParticleRenderer } from './hooks/useParticleRenderer';
import { useWebWorker } from './hooks/useWebWorker';
import { useCanvasAlignment } from './hooks/useCanvasAlignment';
import styles from './TextParticles.scss';
import { AnimationType, type AnimationState } from './TextParticles.types';
import { getCanvasData } from './utils/canvas-utils';
import { getMainThreadMessages } from './worker/messages';
import { getEffectConfiguration, hasAnimationType } from './selectors';
import { useTextHandlers } from './hooks/useTextHandlers';

const TextParticles: React.ForwardRefRenderFunction<
  TextParticlesImperativeActions,
  TextParticlesProps
> = (props, ref) => {
  const {
    id,
    text,
    componentViewMode,
    isPresetPreview,
    reducedMotion: reducedMotionProp = false,
    className,
    customClassNames = [],
    tagName,
    textDirection,
    link,
    isDecorative,
    onPlay,
    onStop,
    onMouseEnter,
    onMouseLeave,
    onPause,
    pauseOnHover = true,
  } = props;
  const SelectableTextElement = (tagName || 'p') as React.ElementType;
  const isEditor = componentViewMode === 'editor';
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textRef = useRef<HTMLHeadingElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const { imageProps, effectProps } = useComputedStyle({
    compId: id,
    elementRef: rootRef,
    isEditor,
    textRef,
  });
  const [reducedMotion, setReducedMotion] = useState(false);
  const [showSelectableText, setShowSelectableText] = useState(true);

  const applyReducedMotion = (reducedMotion && !isEditor) || isPresetPreview;
  const worker = useWebWorker();
  const isAnimationReady = Boolean(worker && imageProps && effectProps);

  useEffect(() => {
    setReducedMotion(reducedMotionProp);
  }, [reducedMotionProp]);

  const effectConfiguration = useMemo(
    () => getEffectConfiguration(effectProps, isEditor),
    [effectProps, isEditor],
  );

  const animationType = hasAnimationType(effectConfiguration)
    ? effectConfiguration.animationType
    : null;

  useEffect(() => {
    if (animationType === AnimationType.OUT) {
      setShowSelectableText(true);
    }
  }, [animationType]);

  const updateAnimationState = useCallback(
    (
      canvas: React.MutableRefObject<HTMLCanvasElement | null>,
      newAnimationState: AnimationState,
    ) => {
      if (canvas.current) {
        canvas.current.setAttribute(
          TestIds.viewer.animationState,
          newAnimationState,
        );
      }

      if (!rootRef.current) {
        return;
      }

      const hasDoneClass = rootRef.current.classList.contains(
        formatClassNames(semanticClassNames.rootDone),
      );

      if (
        hasDoneClass &&
        newAnimationState !== 'finished' &&
        newAnimationState !== 'paused'
      ) {
        rootRef.current.classList.remove(
          formatClassNames(semanticClassNames.rootDone),
        );
      }

      if (!hasDoneClass && newAnimationState === 'finished') {
        rootRef.current.classList.add(
          formatClassNames(semanticClassNames.rootDone),
        );
      }
    },
    [],
  );

  const canvasData = useMemo(
    () => getCanvasData(imageProps?.boundingBox),
    [imageProps?.boundingBox],
  );
  const images = useImageLoader({
    canvasData,
    imageProps,
    text,
    textDirection,
  });
  const messages = useMemo(() => getMainThreadMessages(id), [id]);

  const onAnimationEnd = useCallback(() => {
    if (isEditor) {
      worker?.postMessage(messages.getSetPreviewMessage());
    } else {
    }

    if (animationType) {
      setShowSelectableText(animationType === AnimationType.IN);
    }
  }, [worker, messages, isEditor, animationType, setShowSelectableText]);

  const onAnimationStopped = useCallback(() => {
    if (isEditor) {
      worker?.postMessage(messages.getSetPreviewMessage());
    }

    if (animationType) {
      setShowSelectableText(animationType === AnimationType.OUT);
    }
  }, [isEditor, animationType, worker, messages]);

  const onInitialized = useCallback(() => {
    if (isEditor) {
      worker?.postMessage(messages.getSetPreviewMessage());
    }
  }, [isEditor, worker, messages]);

  const onAnimationStateUpdated = useCallback(
    (animationState: AnimationState) =>
      updateAnimationState(canvasRef, animationState),
    [updateAnimationState],
  );

  const { play, stop, pause } = useParticleRenderer({
    effectConfiguration,
    images,
    canvasRef,
    onAnimationEnd,
    componentId: id,
    worker,
    messages,
    onInitialized,
    onAnimationStopped,
    onAnimationStateUpdated,
  });

  const handlePlay = useCallback(async () => {
    onPlay?.({ type: 'onPlay', compId: id });

    if (animationType) {
      setShowSelectableText(animationType === AnimationType.OUT);
    }

    await play();
  }, [play, onPlay, id, animationType]);

  const handlePause = useCallback(async () => {
    onPause?.({ type: 'onPause', compId: id });
    await pause();
  }, [pause, onPause, id]);

  const handleStop = useCallback(async () => {
    onStop?.({ type: 'onStop', compId: id });
    await stop();

    if (animationType) {
      setShowSelectableText(animationType === AnimationType.OUT);
    }

    onAnimationStopped();
  }, [onStop, id, stop, animationType, onAnimationStopped]);

  const handleStopRef = useRef(handleStop);

  useImperativeHandle(
    ref,
    () => ({
      play: handlePlay,
      stop: handleStop,
      pause: handlePause,
    }),
    [handlePlay, handleStop, handlePause],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const textElement = textRef.current;

    if (isEditor || !isAnimationReady || !canvas || !textElement) {
      return;
    }

    const observer = new IntersectionObserver(
      async entries => {
        if (canvas.getAttribute(TestIds.viewer.animationState) === 'finished') {
          return;
        }

        for (const entry of entries) {
          if (entry.isIntersecting) {
            await handlePlay();
          } else {
            await handleStop();
          }
        }
      },
      { root: null, threshold: 0.5 },
    );

    observer.observe(textElement);

    return () => {
      observer.unobserve(textElement);
    };
  }, [isEditor, handlePlay, handleStop, isAnimationReady]);

  useEffect(
    () => () => {
      worker?.postMessage(messages.getTerminateInstanceMessage());
    },
    [id, worker, messages],
  );

  useEffect(() => {
    const animationState =
      canvasRef.current?.getAttribute(TestIds.viewer.animationState) ?? '';
    if (animationState && !['stopped', 'preview'].includes(animationState)) {
      handleStopRef.current?.();
    }
  }, [text, canvasRef, handleStopRef]);

  const { onMouseEnterText, onMouseLeaveText, onTextClick } = useTextHandlers({
    effectConfiguration,
    isAnimationReady,
    applyReducedMotion,
    handlePause,
    handlePlay,
    pauseOnHover,
    canvasRef,
  });

  const canvasTransform = useCanvasAlignment({
    computedTextAlign: imageProps?.textAlign,
    rootRef,
    textRef,
    canvasData,
  });

  const selectableTextProps = {
    ref: textRef,
    'data-testid': TestIds.viewer.text,
    onClick: !isEditor && !isPresetPreview ? onTextClick : undefined,
    onMouseEnter: onMouseEnterText,
    onMouseLeave: onMouseLeaveText,
    className: classNames(styles.selectableText, {
      [styles.reducedMotion]: applyReducedMotion,
      [styles.hidden]: !showSelectableText,
    }),
  };

  return (
    <div
      {...(isDecorative && { 'aria-hidden': true })}
      id={id}
      ref={rootRef}
      className={classNames(
        styles.root,
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      dir={textDirection}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {applyReducedMotion ? null : (
        <canvas
          aria-hidden={true}
          ref={canvasRef}
          className={styles.canvas}
          data-testid={TestIds.viewer.canvas}
          style={
            canvasData
              ? {
                  width: canvasData.displayWidth,
                  height: canvasData.displayHeight,
                  transform: canvasTransform,
                }
              : undefined
          }
        />
      )}
      {link ? (
        <Link
          {...link}
          className={styles.link}
          dataTestId={TestIds.viewer.link}
        >
          <SelectableTextElement {...selectableTextProps}>
            {text}
          </SelectableTextElement>
        </Link>
      ) : (
        <SelectableTextElement {...selectableTextProps}>
          {text}
        </SelectableTextElement>
      )}
    </div>
  );
};

export default React.forwardRef(TextParticles);
